import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useAppStateStore } from '@/features/application'
import type { RattachementDTO } from '@/features/api/extranet/dto/Profil'
import { useRattachementsStore } from '../rattachements/useRattachementsStore.ts'
import type { ContactAktoDTO } from '@/features/api/extranet/dto/shared'
import { useContactsFavorisStore, type ContactAvecFavoris } from './useContactsFavorisStore'
import * as api from '@/features/services/contacts/ContactsServices'

export const useContactStore = defineStore('contacts', () => {
  const rattachementsStore = useRattachementsStore()
  const favorisStore = useContactsFavorisStore()

  const { rattachements } = storeToRefs(rattachementsStore)
  const appState = useAppStateStore()

  const canLoad = computed(() => {
    return appState.estUtilisateurAuthentifie && appState.estAuthentificationEnCours === false
  })

  const contactsInterne = ref<ContactAktoDTO[]>([])
  const contactModèle = ref('')
  const rattachementModel = ref<RattachementDTO | undefined>()

  async function chargerContacts() {
    if (!canLoad.value) {
      contactsInterne.value = []
      return
    }

    const [result] = await Promise.all([
      api.loadContacts(),
      favorisStore.chargerContactsFavoris(),
    ])
    contactsInterne.value = result ?? []

    // Filtre les favoris non valides
    const emailsContacts = contactsInterne.value.map((contact) => contact.email!)
    await favorisStore.nettoyerFavoris(emailsContacts)
  }

  const contacts = computed<ContactAvecFavoris[]>(() => {
    return contactsInterne.value.map((contact) => ({
      ...contact,
      estFavoris: favorisStore.estContactFavori(contact.email!),
    }))
  })

  const mesContactsFavoris = computed(() => {
    return contacts.value.filter((contact) => contact.estFavoris)
  })

  const homeContact = computed(() => {
    const nonFavoris = contacts.value.filter((c) => !c.estFavoris)
    return mesContactsFavoris.value.concat(nonFavoris).slice(0, 3)
  })

  const contactsFiltrés = computed(() => {
    return contacts.value.filter(filtreFullText).filter(filtreRattachements)
  })

  function filtreFullText(contact: ContactAktoDTO) {
    const filtre = (contactModèle.value || '').trim().toLocaleLowerCase()
    if (!filtre) return true

    const textContact = [contact.nom, contact.prenom, contact.email, contact.fonction]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()

    return (
      (textContact.includes(filtre) ||
        contact.entites?.some((entreprise) =>
          [entreprise?.denomination, entreprise?.siren, entreprise?.nic]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase()
            .includes(filtre),
        )) ??
      false
    )
  }

  function filtreRattachements(contact: ContactAktoDTO): boolean {
    if (!rattachementModel.value) return true
    const rattachement = rattachementModel.value

    return (
      contact.entites?.some((entreprise) => {
        if (rattachement.typeEntiteJuridique === 'Entreprise') {
          return entreprise.siren === rattachement.entite.siren
        } else if (rattachement.typeEntiteJuridique === 'Etablissement') {
          return (
            entreprise.siren === rattachement.entite.siren &&
            (entreprise.nic == null || entreprise.nic === rattachement.entite.nic)
          )
        }
        return false
      }) ?? false
    )
  }

  watch(() => `${canLoad.value}_${appState.contextCourant}`, chargerContacts)

  return {
    contactModèle,
    contacts,
    contactsFiltrés,
    mesContactsFavoris,
    rattachements,
    chargerContacts,
    rattachementModel,
    homeContact,
  }
})
